import React from 'react';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText } from '../../../../styling/stylingHelper';

/**
 * Markdown-*light* renderer for the agent's prose — the only two things the system prompt is
 * allowed to emit: `**bold**` and `- ` bullets. Everything else renders as literal text.
 *
 * Deliberately not a markdown library: the agent's output is model-generated, so it renders as
 * React elements only (never `dangerouslySetInnerHTML`), which makes injection impossible.
 */

const BOLD_SPLIT = /(\*\*[^*\n]+\*\*)/g;

/** Split one line into plain text + `**bold**` runs. */
function renderInline(line: string, keyPrefix: string): React.ReactNode[] {
  return line.split(BOLD_SPLIT).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-${i}`} className={semiBoldText}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });
}

const BULLET = /^\s*[-*]\s+/;

type Chunk = { kind: 'p'; lines: string[] } | { kind: 'ul'; lines: string[] };

/** Group the raw text into paragraphs and bullet lists (a blank line always breaks a chunk). */
function toChunks(text: string): Chunk[] {
  const chunks: Chunk[] = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      // A blank line closes the current chunk.
      if (chunks.length > 0) chunks.push({ kind: 'p', lines: [] });
      continue;
    }
    const isBullet = BULLET.test(line);
    const last = chunks[chunks.length - 1];
    const kind: Chunk['kind'] = isBullet ? 'ul' : 'p';
    const content = isBullet ? line.replace(BULLET, '') : line;
    if (last && last.kind === kind && last.lines.length > 0) last.lines.push(content);
    else chunks.push({ kind, lines: [content] });
  }
  return chunks.filter((chunk) => chunk.lines.length > 0);
}

export const AgentText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  if (!text.trim()) return null;
  return (
    <div className={cn(primaryText, 'text-sm leading-6 flex flex-col gap-2', className)}>
      {toChunks(text).map((chunk, ci) =>
        chunk.kind === 'ul' ? (
          <ul key={ci} className="list-disc pl-5 space-y-1">
            {chunk.lines.map((line, li) => (
              <li key={li}>{renderInline(line, `${ci}-${li}`)}</li>
            ))}
          </ul>
        ) : (
          <p key={ci} className="whitespace-pre-wrap">
            {chunk.lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && '\n'}
                {renderInline(line, `${ci}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        )
      )}
    </div>
  );
};

export default AgentText;
